import { findObject } from "@/helper/array";
import {
  MUTATION_ADD_PERMIT_DECREE_LOCATION,
  MUTATION_REMOVE_PERMIT_DECREE_LOCATION,
  MUTATION_UPDATE_PERMIT_DECREE_LOCATION,
  QUERY_PERMIT_DECREE_LOCATIONS,
} from "@/services/web/api-permit-location.const";
import { PermitLocation, PermitLocationPoint } from "@/types/decree.type";
import { PermitDecreesQuery, DecreeCategory } from "@/types/gql/graphql";
import { useContext, useMemo } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "contexts/city-context-provider";

export const useQueryPermitLocation = () => {
  const { currentCity } = useContext(CityContext);

  const variables = useMemo(
    () => ({
      permitDecreesInput: {
        cityId: currentCity?.id ?? "",
        category: [DecreeCategory.Permit],
      },
    }),
    [currentCity?.id],
  );

  const { data = { permitDecrees: [] }, loading } = useTrackedQuery<PermitDecreesQuery>(QUERY_PERMIT_DECREE_LOCATIONS, {
    variables,
    skip: !currentCity?.id,
    actionName: "QueryPermitDecreeLocations",
  });

  const refetchQueriesConfig = [
    {
      query: QUERY_PERMIT_DECREE_LOCATIONS,
      variables,
    },
  ];

  const [createPermitLocation] = useTrackedMutation(MUTATION_ADD_PERMIT_DECREE_LOCATION, {
    actionName: "AddPermitDecreeLocation",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const [updatePermitDecreeLocation] = useTrackedMutation(MUTATION_UPDATE_PERMIT_DECREE_LOCATION, {
    actionName: "UpdatePermitDecreeLocation",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const [removePermitLocation] = useTrackedMutation(MUTATION_REMOVE_PERMIT_DECREE_LOCATION, {
    actionName: "RemovePermitDecreeLocation",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const permitLocationData: PermitLocationPoint[] = useMemo(() => {
    if (data?.permitDecrees) {
      const newPermitLocation: PermitLocationPoint[] = [];

      data.permitDecrees.forEach((item) => {
        if (item?.locations) {
          item.locations.forEach(({ position, ...rest }) => {
            newPermitLocation.push({
              ...rest,
              coordinates: position.coordinates,
              selectedDecrees: [{ value: item.id, label: item.name }],
            });
          });
        }
      });
      return newPermitLocation;
    }
    return [];
  }, [data]);

  const updatePermitLocation = async (input: PermitLocation) => {
    const fPermitLocation = findObject(permitLocationData, "id", input.id);
    const findDecreeIdx = findObject(data.permitDecrees, "id", input.selectedDecrees[0].value);
    if (permitLocationData[fPermitLocation].selectedDecrees[0].value === input.selectedDecrees[0].value) {
      //Update a permit location in a decree
      if (findDecreeIdx > -1) {
        const newDecreeLocation = {
          decreeId: data.permitDecrees[findDecreeIdx].id,
          location: {
            id: input.id,
            position: input.position,
            address: input.address,
          },
          locationId: input.id,
        };
        await updatePermitDecreeLocation({ variables: newDecreeLocation });
      }
    } else {
      if (findDecreeIdx > -1) {
        //remove the permit location from old decree
        await removePermitLocation({
          variables: { locationId: input.id, decreeId: permitLocationData[fPermitLocation].selectedDecrees[0].value },
        });

        //add the permit location to new decree
        const formatedLocation = {
          position: input.position,
          address: input.address,
        };

        const newDecree = {
          decreeId: data.permitDecrees[findDecreeIdx].id,
          location: formatedLocation,
        };

        await createPermitLocation({ variables: newDecree });
      }
    }
  };

  return {
    permitLocationData,
    loading,
    createPermitLocation,
    updatePermitLocation,
    removePermitLocation,
  };
};
